/**
 * Factory Config Schemas — one Zod schema per `factory.*` configuration row.
 *
 * Every setting the software factory's control loop reads lives in
 * `SystemConfiguration` (category `SYSTEM_CONFIG`, `schoolId` null) behind a
 * typed getter, never in code and never in an environment variable (KTD13).
 * Ruben changes a cap, a budget, a model or the kill switch by editing a row —
 * no deploy, no promotion.
 *
 * EVERY SCHEMA HERE FAILS CLOSED, and that is the whole design. A config row
 * that is absent, malformed, or hand-edited into nonsense must degrade toward
 * "do less", never toward "spawn more VMs" or "spend more money": an invalid
 * `factory.mode` reads as `off`, an absent `factory.gates` means nobody is
 * allowed to approve or resume, and an absent `factory.linear` means "not
 * configured" rather than a default team someone did not choose. The getters
 * that apply those fallbacks are U2's (`services/factory/config.ts`); this file
 * is the shape they parse against, and the defaults below are the ones the
 * schema itself can safely supply.
 *
 * THE DEFAULTS ARE DELIBERATELY SPLIT. `caps`, `timeouts`, `retry`, `budgets`
 * and `assessment` carry schema-level defaults, because a missing number there
 * has one obviously-safe value. `mode`, `linear` and `gates` carry NONE: their
 * safe value is not a number but a refusal, and a schema default would quietly
 * manufacture permission or identity that no human granted.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-12-1318-feat-software-factory-phase-1-control-loop-plan.md
 */

import { z } from 'zod'

import { AgentRunLaneNameSchema, canonicalLaneKeys, type AgentRunLane } from './agent-run'
import { AGENT_PHASE_NAMES, AgentPhaseNameSchema, PhaseNameSchema, type AgentPhaseName } from './agent-run-phases'

/**
 * A map keyed by lane. A row seeded before SCH-2309 may still be keyed by an old
 * lane name (`bug`, `improve`, `ship`); it parses to the new name, and a new key
 * beside it wins ({@link canonicalLaneKeys}). Any other key is still a parse
 * error, so a typo'd lane cannot become a lane that silently never runs.
 */
function laneKeyedRecord<V extends z.ZodType>(value: V) {
  return z.preprocess(canonicalLaneKeys, z.partialRecord(AgentRunLaneNameSchema, value))
}

// =============================================================================
// factory.mode — the kill switch
// =============================================================================

/**
 * The global kill switch (R18), read every tick.
 *
 * - `on`    — normal operation.
 * - `drain` — start no VM: no intake, no dispatch, no retry, no resume.
 *   Running work finishes. This is the "stop digging" setting.
 * - `off`   — `drain`, plus cancel running and parked runs and destroy their
 *   VMs. The success criterion is that `off` leaves zero factory VMs within
 *   two ticks.
 *
 * NO DEFAULT, ON PURPOSE. An absent or invalid row reads as `off` in U2's
 * getter — the loudest possible failure, and the only one that cannot run up a
 * bill or leave VMs alive. `enabled` is deliberately not a member: a boolean
 * cannot express `drain`, and a config someone "fixed" to `true` must be
 * refused rather than interpreted as `on`.
 */
export const FACTORY_MODES = ['on', 'drain', 'off'] as const

export const FactoryModeSchema = z.enum(FACTORY_MODES)
export type FactoryMode = z.infer<typeof FactoryModeSchema>

// =============================================================================
// factory.lanes — per-lane enable
// =============================================================================

/**
 * Per-lane enable flag (R18), so one misbehaving lane can be stopped without
 * taking the whole factory down.
 *
 * A lane absent from the record is DISABLED, not enabled — the same fail-closed
 * rule as `factory.mode`. Keyed by {@link AgentRunLaneNameSchema} rather than a
 * free string so a typo'd lane name is a parse error rather than a lane that
 * silently never runs.
 */
export const FactoryLanesSchema = z.object({
  enabled: laneKeyedRecord(z.boolean()).default({})
})
export type FactoryLanes = z.infer<typeof FactoryLanesSchema>

// =============================================================================
// factory.caps — concurrency
// =============================================================================

/**
 * How many runs may be in flight at once (R7).
 *
 * `executors.vm` IS THE exe.dev POOL LIMIT, not a count of `RUNNING` runs. The
 * dispatcher counts every run with a VM it has not yet destroyed, across every
 * status — parked and grace-period VMs included — because a parked VM occupies
 * the same 16 GB / 4 vCPU pool as a busy one (KTD8). Counting only `RUNNING`
 * would oversubscribe the pool with exactly the runs nobody is watching.
 *
 * Defaults are 3 and 3 until the exe.dev pool-tier quote lands; raising them is
 * a config edit, not a code change.
 */
export const FactoryCapsSchema = z.object({
  /** Total concurrent runs across every lane and executor. */
  global: z.number().int().min(0).default(3),
  /** Per-lane ceiling. A lane absent from the record is bounded only by `global`. */
  lanes: laneKeyedRecord(z.number().int().min(0)).default({}),
  executors: z
    .object({
      /** Live VMs, counted across statuses — see the note above. */
      vm: z.number().int().min(0).default(3)
    })
    // `.prefault`, not `.default`: Zod 4 hands a `.default` value straight back
    // WITHOUT parsing it, so `.default({})` would yield a literal `{}` and the
    // inner `vm: 3` would never apply — a cap of `undefined` reads as no cap.
    .prefault({})
})
export type FactoryCaps = z.infer<typeof FactoryCapsSchema>

// =============================================================================
// factory.executors — per-executor settings
// =============================================================================

/**
 * How the `vm` executor drives exe.dev.
 *
 * `transport` selects between the HTTPS `/exec` default and the ssh fallback
 * (KTD1). Both take the same command strings from one builder, so this is a
 * genuine swap rather than two code paths; ssh exists for local debugging and
 * an HTTPS outage.
 *
 * `startFailureCooldownMs` is the exe.dev-outage brake (KTD5). A start failure
 * before any VM exists spends no attempt, so without a cooldown an expired
 * token would spin the whole queue through the dispatcher on every tick. After
 * three consecutive such failures, dispatch pauses for this long and the tick
 * result names exe.dev as unavailable.
 */
export const FACTORY_VM_TRANSPORTS = ['https', 'ssh'] as const

export const FactoryVmTransportSchema = z.enum(FACTORY_VM_TRANSPORTS)
export type FactoryVmTransport = z.infer<typeof FactoryVmTransportSchema>

/**
 * The Claude subscriptions a factory VM may run as (SCH-1862).
 *
 * THE NAMES ARE THE ENV SUFFIXES, LOWER-CASED. `tooling/coordinator/account-usage`
 * discovers accounts by the suffix of `CLAUDE_CODE_OAUTH_TOKEN_<NAME>` in the
 * agent environment, so `ruben` here is `CLAUDE_CODE_OAUTH_TOKEN_RUBEN` there
 * and the two vocabularies cannot drift into each other's spelling.
 *
 * A CLOSED ENUM RATHER THAN A FREE STRING, because the only thing this value
 * does is name an environment key to copy. A typo in a free string would make
 * `GET /agent-env` fail closed at run time, on a VM, for a run that has already
 * been claimed — the cheapest place to catch it is the config write.
 */
export const FACTORY_VM_ACCOUNTS = ['ruben', 'mianala', 'jamie', 'diego', 'is'] as const

/*
 * FACTORY_VM_ACCOUNTS is the BUILT-IN Claude accounts only (mirrors `CLAUDE_ACCOUNTS` in
 * apps/ops-store/convex/accountRegistry.ts). Accounts added in the store (`poolAccounts`, SCH-4501) are data, so any
 * contract carrying a name the store returns or accepts uses `ClaudeAccountNameSchema` below (SCH-4591).
 */

export const FactoryVmAccountSchema = z.enum(FACTORY_VM_ACCOUNTS)
export type FactoryVmAccount = z.infer<typeof FactoryVmAccountSchema>

/** The platform a pool account bills to: a Claude subscription, or a ChatGPT subscription through exe.dev. */
export const POOL_ACCOUNT_PLATFORMS = ['claude', 'chatgpt'] as const
export const PoolAccountPlatformSchema = z.enum(POOL_ACCOUNT_PLATFORMS)
export type PoolAccountPlatform = z.infer<typeof PoolAccountPlatformSchema>

/**
 * A pool account's name, on either platform: the Claude names above, `ruben-gpt`, or an added ChatGPT account.
 * Names are unique across both platforms, and an added one may not reuse a built-in name, `chatgpt` or `llm` (the
 * store enforces that part). An added account's exe.dev integration is `llm-<name>`. Mirrored by `POOL_ACCOUNT_NAME`
 * in apps/ops-store/convex/accounts.ts. `auto` is never a name: it means "no named account" wherever an account is set.
 */
export const PoolAccountNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{1,30}$/)
  .refine(name => name !== 'auto', 'auto is not an account name')
export type PoolAccountName = z.infer<typeof PoolAccountNameSchema>

/**
 * Any active Claude account, built-in or added in the store (SCH-4591). The store checks membership against its
 * registry (`claudeAccountNames`); the schema checks the shape only, so an added account parses wherever the store
 * takes or returns one.
 */
export const ClaudeAccountNameSchema = PoolAccountNameSchema
export type ClaudeAccountName = z.infer<typeof ClaudeAccountNameSchema>

/*
 * The Claude pool's roster settings, as the ops store's roster stores them (strings and numbers only):
 * `factory.accounts.mode`, `factory.accounts.order`, `accounts.<name>.enabled` and
 * `accounts.<name>.fiveHourCeiling`. The store's grammar (apps/ops-store/contract/scotty-config.ts,
 * convex/config.ts) checks the same values; a person-facing setter converts to and from a list and a boolean.
 */

/** `balance`: room-per-hour scoring (the default). `ordered`: the first eligible account in the order. */
export const FACTORY_POOL_MODES = ['balance', 'ordered'] as const
export const FactoryPoolModeSchema = z.enum(FACTORY_POOL_MODES)
export type FactoryPoolMode = z.infer<typeof FactoryPoolModeSchema>

/** `accounts.<name>.enabled`: `off` never leases the account; unset is `on`. */
export const FactoryAccountEnabledSchema = z.enum(['on', 'off'])
export type FactoryAccountEnabled = z.infer<typeof FactoryAccountEnabledSchema>

/**
 * `factory.accounts.order`: Claude account names (built-in or added) joined by commas with no spaces, at least one,
 * none twice. The store checks that each is an active account; the schema checks each name's shape.
 */
export const FactoryAccountOrderSchema = z
  .string()
  .min(1, 'at least one account name')
  .refine(
    value => value.split(',').every(name => ClaudeAccountNameSchema.safeParse(name).success),
    'a comma-separated list of account names, no spaces'
  )
  .refine(value => new Set(value.split(',')).size === value.split(',').length, 'no account may appear twice')
export type FactoryAccountOrder = z.infer<typeof FactoryAccountOrderSchema>

/** `accounts.<name>.fiveHourCeiling`: a whole 5-hour percent; unset is the store's global ceiling. */
export const FactoryAccountFiveHourCeilingSchema = z.number().int().min(0).max(100)
export type FactoryAccountFiveHourCeiling = z.infer<typeof FactoryAccountFiveHourCeilingSchema>

export const FactoryExecutorsSchema = z.object({
  vm: z
    .object({
      /** The golden exe.dev image every run VM is copied from. */
      baseImage: z.string().min(1).default('sj-base'),
      transport: FactoryVmTransportSchema.default('https'),
      /**
       * The AWS Secrets Manager secret `GET /agent-env` serves (R10, KTD3).
       *
       * CONFIG RATHER THAN A CONSTANT because the secret is the one piece of
       * the agent environment that differs between a staging account and
       * production, and because least-privilege per-lane secrets are deferred
       * follow-up work — when they land, this is the key that splits. The
       * default is the secret Ruben creates in the tail-ownership step, so a
       * deployment with no `factory.executors` row still reaches the right
       * name instead of failing closed on a missing setting nobody knew to
       * write.
       */
      agentEnvSecretId: z.string().min(1).default('agents/env'),
      startFailureCooldownMs: z
        .number()
        .int()
        .min(0)
        .default(5 * 60_000),
      /**
       * Which account's token `GET /agent-env` serves (SCH-1862, interim).
       *
       * NO DEFAULT, and the absence is the point. `agents/env` holds one
       * suffixed token per subscription and no bare `CLAUDE_CODE_OAUTH_TOKEN`;
       * defaulting to one of the four would silently spend a named person's
       * subscription on every run of a deployment nobody configured. Unset
       * means `GET /agent-env` fails closed (503) instead of guessing.
       *
       * PER-RUN, USAGE-AWARE SELECTION IS NOT THIS FIELD. One account for the
       * whole executor is the interim answer; SCH-1862 option 1 is the
       * rotation that replaces it.
       */
      account: FactoryVmAccountSchema.optional()
    })
    // `.prefault` for the same reason as `factory.caps` above: a `.default({})`
    // is returned unparsed, so the transport would come back `undefined`.
    .prefault({})
})
export type FactoryExecutors = z.infer<typeof FactoryExecutorsSchema>

// =============================================================================
// factory.timeouts — liveness, retry and budget
// =============================================================================

/**
 * When the control plane kills a run, and what happens next (R13, R14, R15).
 *
 * TWO LIVENESS TIMEOUTS, NOT ONE, because they catch opposite failures
 * (KTD14): `stallMs` is heartbeat age, which catches a VM that died, and
 * `progressMs` is agent-output age, which catches a session that is alive and
 * producing nothing. A single timeout would have to be the looser of the two
 * and would miss whichever failure it was not tuned for.
 *
 * Every duration has a `.min(1)`: a zero timeout means "kill everything
 * immediately", which reads as a plausible number and is never what anyone
 * meant. Zero attempts and a zero budget are likewise refused — a run that can
 * never start is a config error, not a policy.
 */
export const FactoryTimeoutsSchema = z.object({
  /** Heartbeat age past which the run is stalled. Default 5 minutes. */
  stallMs: z
    .number()
    .int()
    .min(1)
    .default(5 * 60_000),
  /** Agent-output age past which the session is hung. Default 20 minutes. */
  progressMs: z
    .number()
    .int()
    .min(1)
    .default(20 * 60_000),
  /** Wall-clock ceiling per run, overridable per lane. Default 2 hours. */
  maxRunMs: z
    .number()
    .int()
    .min(1)
    .default(2 * 60 * 60_000),
  /** Per-lane `maxRunMs` overrides; a lane absent here uses the value above. */
  lanes: laneKeyedRecord(z.number().int().min(1)).default({})
})
export type FactoryTimeouts = z.infer<typeof FactoryTimeoutsSchema>

/**
 * Retry policy (R14). Backoff is `min(baseMs * 2^(attempt-1), maxBackoffMs)`,
 * computed by the tick and stored on the run as `nextAttemptAt`.
 *
 * Only `stall` and `executor` failures reach this at all (KTD5) — see
 * `isRetryableFailure` in `agent-run.ts`.
 */
export const FactoryRetrySchema = z.object({
  /** Total attempts including the first. Default 3. */
  maxAttempts: z.number().int().min(1).default(3),
  baseMs: z.number().int().min(1).default(10_000),
  maxBackoffMs: z
    .number()
    .int()
    .min(1)
    .default(5 * 60_000)
})
export type FactoryRetry = z.infer<typeof FactoryRetrySchema>

/**
 * Cost ceilings in dollars (R15).
 *
 * A run past its budget fails with `budget` and is NEVER retried without a
 * human — the one failure kind where retrying is the expensive mistake. The
 * budget is resolved at dispatch and frozen onto the run, so raising or
 * lowering this row never retroactively kills something already in flight.
 *
 * `shipUnit` is per UNIT of a feature run rather than per run: a feature lane's
 * units are separate runs, and a single shared ceiling would starve the last unit of
 * a large plan.
 */
export const FactoryBudgetsSchema = z.object({
  /** Default ceiling for the defect and improvement lanes. */
  defaultUsd: z.number().positive().default(15),
  /** Default ceiling per feature unit. The key keeps the lane's pre-SCH-2309 name: seeded rows carry it. */
  shipUnitUsd: z.number().positive().default(60),
  /** Per-lane overrides; a lane absent here uses one of the two above. */
  lanes: laneKeyedRecord(z.number().positive()).default({})
})
export type FactoryBudgets = z.infer<typeof FactoryBudgetsSchema>

// =============================================================================
// factory.assessment — the classifier
// =============================================================================

/**
 * The model assessment calls when its RULES cannot decide (R2, R5).
 *
 * Rules run first and decide most tickets; the model is the tie-breaker, which
 * is why it is a config row rather than a pinned constant — OpenRouter pools,
 * Claude and Codex are all first-class here. `fallbackModel` is what a model
 * failure falls back to, and if that fails too the run PARKS and never spawns:
 * an assessment that cannot decide must not guess.
 *
 * `minConfidence` is the park threshold. A low-confidence lane guess parks the
 * run for a human rather than dispatching a VM at the wrong lane.
 *
 * `provider` picks who answers (KTD16 of the operator-layer plan): `openrouter`
 * (the default) asks `model` then `fallbackModel` for a JSON answer;
 * `typesafe` asks TypeSafe's Jev one typed question per assessment field and
 * has no fallback. The provider value is `typesafe`, not `jev`, because
 * `classifierVersion` already defaults to `jev-1` for the OpenRouter
 * classifier and the two must not be confused.
 */
export const FACTORY_UNLABELED_POLICIES = ['park', 'dispatch'] as const

export const FactoryUnlabeledPolicySchema = z.enum(FACTORY_UNLABELED_POLICIES)
export type FactoryUnlabeledPolicy = z.infer<typeof FactoryUnlabeledPolicySchema>

export const FACTORY_ASSESSMENT_PROVIDERS = ['openrouter', 'typesafe'] as const

export const FactoryAssessmentProviderSchema = z.enum(FACTORY_ASSESSMENT_PROVIDERS)
export type FactoryAssessmentProvider = z.infer<typeof FactoryAssessmentProviderSchema>

export const FactoryAssessmentSchema = z.object({
  /** OpenRouter model id. */
  model: z.string().min(1).default('anthropic/claude-sonnet-5'),
  fallbackModel: z.string().min(1).default('anthropic/claude-haiku-4.5'),
  minConfidence: z.number().min(0).max(1).default(0.6),

  // ── Phase runner (U1, U2) ─────────────────────────────────────────────────
  // Every field below has a default, so a row written before them still parses.

  /** Stamped on every persisted classification; bump it when rules, prompt or schema change. */
  classifierVersion: z.string().min(1).max(64).default('jev-1'),
  /** Abort the model call after this long, then try the fallback once. Default 30 seconds. */
  timeoutMs: z.number().int().min(1).default(30_000),
  /**
   * Ask the provider for schema-constrained output. OFF by default: support is
   * per provider on a config-swappable model id, and a rejection falls through
   * to the ordinary parse path, never to a park (KTD2).
   */
  responseFormat: z.boolean().default(false),
  /**
   * What happens to a candidate whose lane came only from the model (KD6,
   * KTD16). `park` — the default until the classifier's precision has been
   * measured — parks it with the suggested lane as its question; `dispatch`
   * sends it to that lane. A label, a resume comment naming the lane, or an
   * override is authoritative and is never subject to this policy.
   */
  unlabeledPolicy: FactoryUnlabeledPolicySchema.default('park'),

  // ── TypeSafe provider (operator-layer plan U5) ────────────────────────────

  /** Who answers the ambiguous remainder. OpenRouter until the classifier eval says otherwise. */
  provider: FactoryAssessmentProviderSchema.default('openrouter'),
  /** TypeSafe model id, read only when `provider` is `typesafe`. */
  typesafeModel: z.string().min(1).default('jev-latest'),
  /**
   * TypeSafe endpoint, read only when `provider` is `typesafe`. HTTPS only:
   * the request carries `TYPESAFE_API_KEY` as a bearer token.
   */
  typesafeEndpoint: z.url({ protocol: /^https$/ }).default('https://api.typesafe.ai/v1/systemone')
})
export type FactoryAssessment = z.infer<typeof FactoryAssessmentSchema>

// =============================================================================
// factory.routing — runner and model per lane and phase
// =============================================================================

/**
 * Which runner and model a lane's phase gets (R11), read by the executor at
 * start.
 *
 * PHASE 1 STORES MORE THAN IT USES, deliberately. One headless session runs the
 * whole lane, so only the `lead` entry is launched; entries for other phases,
 * and every `codex` entry, are stored and returned so that Phase 3's per-phase
 * envelopes are a config edit rather than a schema change.
 *
 * Resolution is lane-phase, then the lane's `lead`, then `default` (U2's
 * `resolveRouting`). An unknown runner is a parse error rather than a silent
 * fallback: launching the wrong binary is not a degradation, it is a different
 * program.
 */
export const FACTORY_RUNNERS = ['claude', 'codex'] as const

export const FactoryRunnerSchema = z.enum(FACTORY_RUNNERS)
export type FactoryRunner = z.infer<typeof FactoryRunnerSchema>

export const FACTORY_EFFORTS = ['low', 'medium', 'high'] as const

export const FactoryEffortSchema = z.enum(FACTORY_EFFORTS)
export type FactoryEffort = z.infer<typeof FactoryEffortSchema>

export const FactoryRoutingEntrySchema = z.object({
  runner: FactoryRunnerSchema,
  model: z.string().min(1),
  effort: FactoryEffortSchema.default('medium')
})
export type FactoryRoutingEntry = z.infer<typeof FactoryRoutingEntrySchema>

/**
 * The lanes the phase runner serves, whose routing keys are phase names.
 *
 * On these lanes a routing key must be `lead` or an AGENT phase name (KTD22): a
 * code phase runs no model, so a `commit` entry would be configuration nothing
 * reads, and a typo'd phase would silently fall through to `lead`. The team
 * lanes (`feature`) keep free keys — their `plan` and `review` are team-lead
 * phases, not phase-runner steps.
 */
export const FACTORY_PHASE_RUNNER_LANES = [
  'defect',
  'improvement',
  'investigate'
] as const satisfies readonly AgentRunLane[]
type FactoryPhaseRunnerLane = (typeof FACTORY_PHASE_RUNNER_LANES)[number]

/**
 * The agent phases each phase-runner lane runs, mirroring its step list in
 * `tooling/coordinator/lanes/<lane>.ts` (the lane's own test asserts the
 * match). The KTD26 budget floor and the brief's per-phase routing are taken
 * over THIS list, never over every agent phase: the investigate lane's
 * `research` is not a defect-lane phase, and counting it there would raise the
 * defect lane's floor by a phase it never runs.
 */
export const PHASE_RUNNER_LANE_AGENT_PHASES: Readonly<Record<FactoryPhaseRunnerLane, readonly AgentPhaseName[]>> = {
  defect: ['triage', 'build', 'address-findings', 'retro'],
  improvement: ['triage', 'build', 'address-findings', 'retro'],
  investigate: ['research', 'retro']
}

/** The one key a phase-runner lane's routing may carry besides an agent phase: the single-session lead. */
export const LEAD_ROUTING_KEY = 'lead'

export function isPhaseRunnerLane(lane: string): lane is FactoryPhaseRunnerLane {
  return (FACTORY_PHASE_RUNNER_LANES as readonly string[]).includes(lane)
}

export const FactoryRoutingSchema = z.object({
  /** Last-resort entry when neither a lane-phase nor a lane `lead` entry exists. */
  default: FactoryRoutingEntrySchema.default({ runner: 'claude', model: 'opus', effort: 'medium' }),
  /** `lanes.<lane>.<phase>`; `lead` is the phase Phase 1 actually launches. */
  lanes: laneKeyedRecord(z.record(z.string(), FactoryRoutingEntrySchema))
    .default({})
    .superRefine((lanes, ctx) => {
      for (const [lane, phases] of Object.entries(lanes)) {
        if (!isPhaseRunnerLane(lane) || !phases) continue
        for (const key of Object.keys(phases)) {
          if (key === LEAD_ROUTING_KEY || (AGENT_PHASE_NAMES as readonly string[]).includes(key)) continue
          ctx.addIssue({
            code: 'custom',
            path: [lane, key],
            message: `${key} is not an agent phase; ${lane} routing keys are ${LEAD_ROUTING_KEY} or ${AGENT_PHASE_NAMES.join(', ')}`
          })
        }
      }
    })
})
export type FactoryRouting = z.infer<typeof FactoryRoutingSchema>

// =============================================================================
// factory.phases — per-phase caps for the phase runner
// =============================================================================

/**
 * One agent phase's limits.
 *
 * THE DEFAULTS ARE SMALL ON PURPOSE. A phase past its budget or wall clock
 * PARKS with a `budget` or `silence` violation a human can raise and resume
 * (KTD13), so an unconfigured phase should stop early rather than spend. They
 * also satisfy KTD26 against the default `factory.budgets.defaultUsd` of 15:
 * four agent phases at $1, times one plus the default run-level cap of 2, is
 * $12 (the investigate lane's two, $6).
 */
export const FactoryPhaseCapsSchema = z.object({
  /** Corrections of this phase's session after a failing gate, before it parks. */
  maxCorrections: z.number().int().min(0).default(2),
  /** `--max-budget-usd` for the phase session. */
  budgetUsd: z.number().positive().default(1),
  /** No `result` within this long ends the phase as silence. Default 20 minutes. */
  wallClockMs: z
    .number()
    .int()
    .min(1)
    .default(20 * 60_000)
})
export type FactoryPhaseCaps = z.infer<typeof FactoryPhaseCapsSchema>

/**
 * One lane's phase-runner settings. A lane with an entry here runs through the
 * phase runner; a lane without one stays on the single-session path (KTD9).
 */
export const FactoryLanePhasesSchema = z.object({
  /**
   * The `LANE_VERSION` this row was seeded for. REQUIRED, no default: the
   * runner parks with `config-lane-version` when it differs from the deployed
   * phase list, which is the only thing that catches a row seeded for an older
   * list — a default would make every row look current.
   */
  laneVersion: z.string().min(1).max(64),
  /** Park with `configured-stop` before the first step with this phase name. */
  stopBeforePhase: PhaseNameSchema.optional(),
  /** Per agent phase. A code phase name is a parse error; an absent phase takes the defaults. */
  phases: z.partialRecord(AgentPhaseNameSchema, FactoryPhaseCapsSchema).default({})
})
export type FactoryLanePhases = z.infer<typeof FactoryLanePhasesSchema>

export const FactoryPhasesSchema = z.object({
  /** Corrections across every phase of one run attempt, before it parks (KTD13). */
  maxCorrections: z.number().int().min(0).default(2),
  /** Only the lanes the phase runner serves; a `feature` entry would put a team lane on a path it has no list for. */
  lanes: z
    .preprocess(canonicalLaneKeys, z.partialRecord(z.enum(FACTORY_PHASE_RUNNER_LANES), FactoryLanePhasesSchema))
    .default({})
})
export type FactoryPhases = z.infer<typeof FactoryPhasesSchema>

/** A phase's caps, or the schema defaults when the lane leaves it unconfigured. */
export function resolvePhaseCaps(lane: FactoryLanePhases, phase: AgentPhaseName): FactoryPhaseCaps {
  return lane.phases[phase] ?? FactoryPhaseCapsSchema.parse({})
}

/**
 * The smallest run budget that lets every agent phase spend its own budget on
 * every allowed correction (KTD26): the sum of the lane's agent-phase budgets,
 * times one plus the run-level correction cap.
 *
 * A run budget below this dies at the heartbeat's run ceiling — `failed/budget`,
 * never retried without a human — while its phase budgets are untouched. The
 * backend asserts it when it resolves `factory.phases`; `agentPhases` is the
 * lane's agent steps ({@link PHASE_RUNNER_LANE_AGENT_PHASES}).
 */
export function requiredLaneBudgetUsd(
  lane: FactoryLanePhases,
  runMaxCorrections: number,
  agentPhases: readonly AgentPhaseName[]
): number {
  const perAttempt = agentPhases.reduce((sum, phase) => sum + resolvePhaseCaps(lane, phase).budgetUsd, 0)
  return perAttempt * (1 + runMaxCorrections)
}

// =============================================================================
// factory.linear — ids, resolved by name at seed time
// =============================================================================

/**
 * The Linear ids the loop writes through (KTD12).
 *
 * IDS, NOT NAMES, and that is the point: renaming a label in Linear must not
 * break the loop, so the seeder resolves each name once and stores the stable
 * id. A missing id fails the tick's Linear steps CLOSED with a named error
 * rather than falling back to a guess — writing to the wrong team or moving an
 * issue to the wrong state is worse than not writing at all.
 *
 * NO DEFAULTS ANYWHERE IN THIS SCHEMA. There is no safe default team id.
 *
 * `labels` is keyed by the run's machine STATE, which is why the Kanban states
 * stay human vocabulary: the factory's own state lives in a single-select label
 * group (`factory:*`), and `stateIds` carries only the two coarse board moves
 * it makes (R19, R21).
 */
export const FACTORY_LABEL_ROLES = [
  'eligible',
  'queued',
  'running',
  'parked',
  'landing',
  'done',
  'failed',
  'declined'
] as const

export const FactoryLabelRoleSchema = z.enum(FACTORY_LABEL_ROLES)
export type FactoryLabelRole = z.infer<typeof FactoryLabelRoleSchema>

export const FactoryLinearSchema = z.object({
  teamId: z.string().min(1),
  /** The single-select label group the `factory:*` labels belong to. */
  labelGroupId: z.string().min(1),
  /** Label id per machine state. A role absent here fails that state's sync closed. */
  labels: z.partialRecord(FactoryLabelRoleSchema, z.string().min(1)),
  stateIds: z.object({
    inProgress: z.string().min(1),
    done: z.string().min(1)
  }),
  /**
   * Route KLEOS bug triage to the `investigate` lane (customer issue loop,
   * KTD11): when true, a Sparky bug ticket carries `Investigate` + `eligible`
   * INSTEAD of `Defect`, because assessment takes the first lane label it finds
   * and a ticket carrying both would route unpredictably. A flag, not an id,
   * and optional so every row seeded before it still parses; absent is off.
   */
  investigateBugs: z.boolean().optional()
})
export type FactoryLinear = z.infer<typeof FactoryLinearSchema>

// =============================================================================
// factory.gates — who may approve and who may resume
// =============================================================================

/**
 * LINEAR USER IDS — the two human allowlists, and the security boundary of the
 * whole factory.
 *
 * `schemaApprovers` may post `/factory approve-schema` on a ticket whose run
 * touches `prisma/schema/**` (R25, KTD16). `resumers` may steer a parked run
 * with a comment (R23, KTD19), and that list is the harder of the two: a
 * resumed session runs with `bypassPermissions` and the full agent
 * environment, so a comment from an unlisted user must do NOTHING rather than
 * be fenced as data and passed along anyway.
 *
 * BOTH DEFAULT TO EMPTY, and empty means nobody. A schema, unlike a getter,
 * cannot tell an unconfigured allowlist from an intentionally empty one — so
 * the safe reading is the restrictive one, and a factory that refuses every
 * approval is a visible problem while one that accepts every approval is not.
 *
 * UUIDs are required rather than any non-empty string, which is what refuses an
 * email address here: emails are re-assignable and are not what the Linear API
 * compares against, so an allowlist of addresses would silently match nobody
 * while looking correct.
 */
export const FactoryGatesSchema = z.object({
  schemaApprovers: z.array(z.uuid()).default([]),
  resumers: z.array(z.uuid()).default([])
})
export type FactoryGates = z.infer<typeof FactoryGatesSchema>

// =============================================================================
// Key registry
// =============================================================================

/**
 * Every `factory.*` key, with its schema — one place that answers "what is
 * configurable", so U2's getters and the seed script cannot drift from each
 * other by adding a key to only one of them.
 */
export const FACTORY_CONFIG_SCHEMAS = {
  'factory.mode': FactoryModeSchema,
  'factory.lanes': FactoryLanesSchema,
  'factory.caps': FactoryCapsSchema,
  'factory.executors': FactoryExecutorsSchema,
  'factory.timeouts': FactoryTimeoutsSchema,
  'factory.retry': FactoryRetrySchema,
  'factory.budgets': FactoryBudgetsSchema,
  'factory.assessment': FactoryAssessmentSchema,
  'factory.routing': FactoryRoutingSchema,
  'factory.phases': FactoryPhasesSchema,
  'factory.linear': FactoryLinearSchema,
  'factory.gates': FactoryGatesSchema
} as const

export type FactoryConfigKey = keyof typeof FACTORY_CONFIG_SCHEMAS

export const FACTORY_CONFIG_KEYS = Object.keys(FACTORY_CONFIG_SCHEMAS) as readonly FactoryConfigKey[]
